<template>
  <div class="settings-page integration">
    <h2>Integrations</h2>
    <div class="subtitle">
      Connect your different services to your Routine account.
    </div>
    <div class="tabs-row">
      <BaseTabs
        :tabs-titles="tabTitles"
        :selected-tab="activeTab"
        storage-key="integrations-tab"
        class="integrations-tabs"
        @update:selected-tab="selectTab"
      />
      <BaseInput
        v-model:value="searchQuery"
        placeholder="Search..."
        has-styles
        class="integrations-search"
      >
        <template #left>
          <SearchIcon class="search-icon" />
        </template>
      </BaseInput>
    </div>
    <BaseAlert v-if="infoText" state="info" class="info-text">
      {{ infoText }}
    </BaseAlert>
    <BaseAlert v-if="appListError" state="error" class="integration-error">
      Data integrations could not be loaded. Check the Membrane API
      configuration and try again.
    </BaseAlert>

    <!-- Skeleton for Membrane loading -->
    <section v-if="appListLoading" class="loading-section">
      <div class="card-grid">
        <BaseSkeletonLine v-for="i in 2" :key="i" class="card-skeleton" />
      </div>
    </section>

    <div
      v-else-if="allItems.length === 0 && searchQuery.trim()"
      class="no-results"
    >
      No integrations match your search.
    </div>

    <div v-else class="card-grid">
      <div
        v-for="item in allItems"
        :key="item.id"
        class="card"
        :class="{ connected: item.connected, unavailable: item.unavailable }"
        @click="!item.unavailable && item.onClick()"
      >
        <div class="card-header">
          <component
            :is="item.iconComponent"
            v-if="item.iconComponent"
            class="card-icon"
          />
          <img v-else-if="item.logoUri" :src="item.logoUri" class="card-icon" />
          <img
            v-else-if="item.type === 'google-workspace'"
            src="../../assets/directory.svg"
            class="card-icon"
          />
        </div>
        <span class="card-name">{{ item.name }}</span>
        <span class="card-description">{{ item.description }}</span>
        <a
          v-if="item.unavailable && item.helpUrl"
          :href="item.helpUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="card-help"
          @click.stop
          >?</a
        >
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, toRef, watch } from "vue";
import { useRoute } from "vue-router";

import BaseAlert from "@/components/base/BaseAlert.vue";
import BaseInput from "@/components/base/BaseInput.vue";
import BaseSkeletonLine from "@/components/base/BaseSkeletonLine.vue";
import BaseTabs from "@/components/base/BaseTabs.vue";
import McpIcon from "@/components/icons/apps/McpIcon.vue";
import SearchIcon from "@/components/icons/SearchIcon.vue";
import * as inject from "@/components/inject";
import type { McpCatalogServer } from "@/components/settings/integrations/mcpServersCatalog";
import {
  loadMcpServersCatalog,
  searchMcpServersCatalog,
} from "@/components/settings/integrations/mcpServersCatalog";
import type {
  IntegrationItem,
  IntegrationItemType,
  IntegrationNature,
} from "@/components/settings/integrations/types";
import { useIntegrationsGoogle } from "@/components/settings/integrations/useIntegrationsGoogle";
import { useMembraneList } from "@/components/settings/integrations/useMembraneList";
import { useUnavailableIntegrations } from "@/components/settings/integrations/useUnavailableIntegrations";
import { useReleaser } from "@/ref";
import { RouteNames } from "@/routes/types";
import { navigateTo } from "@/utils/navigate";

// There is no "Data" category for now: Membrane integrations surface under
// "AI" instead, as the assistant, agents and automations can use them.
type IntegrationTab = "Tasks" | "Calendars" | "Contacts" | "AI";

const personalOrgTabTitles = [
  "Tasks",
  "Calendars",
  "Contacts",
  "AI",
] as const satisfies readonly IntegrationTab[];

// Calendars & Contacts integrations are personal (they live on the user's
// personal organization), so those categories are hidden in team orgs.
const teamOrgTabTitles = [
  "Tasks",
  "AI",
] as const satisfies readonly IntegrationTab[];

const natureDescriptions: Record<IntegrationNature, string> = {
  calendars:
    "Calendar integrations bidirectionally synchronize your calendars and events from your calendar providers into Routine to be used in the Agenda and Calendar screens.",
  contacts:
    "Contact integrations provide a 2-way sync between your contacts providers and Routine for those to be referenced in meetings and accessed in the Contacts screen.",
  tasks:
    "Task integrations import and synchronize bidirectionally data from external providers to be manipulated as Routine tasks: plan, block time in calendar, complete etc.",
  ai: "AI integrations connect third-party services that the assistant, agents and automations can use to search, read and act on your behalf.",
};

const natureByType: Record<IntegrationItemType, IntegrationNature> = {
  "google-calendar": "calendars",
  "google-contacts": "contacts",
  "google-workspace": "contacts",
  notion: "tasks",
  zapier: "tasks",
  membrane: "ai",
  mcp: "ai",
  "microsoft-outlook": "calendars",
  "apple-calendar": "calendars",
};

const tabNatureByTab = {
  Calendars: "calendars",
  Tasks: "tasks",
  Contacts: "contacts",
  AI: null,
} satisfies Record<IntegrationTab, IntegrationNature | null>;

const tabToNature = (tab: IntegrationTab): IntegrationNature | null => {
  return tabNatureByTab[tab];
};

const defaultTab: IntegrationTab = "Tasks";

const isIntegrationTab = (tab: string): tab is IntegrationTab => {
  switch (tab) {
    case "Tasks":
    case "Calendars":
    case "Contacts":
    case "AI":
      return true;
    default:
      return false;
  }
};

const queryToTab = (query: unknown): IntegrationTab => {
  if (typeof query !== "string") return defaultTab;
  const map: Record<string, IntegrationTab> = {
    calendars: "Calendars",
    tasks: "Tasks",
    contacts: "Contacts",
    ai: "AI",
  };
  return map[query] ?? defaultTab;
};

export default defineComponent({
  name: "Integrations",
  components: {
    BaseAlert,
    BaseInput,
    BaseSkeletonLine,
    BaseTabs,
    SearchIcon,
  },
  props: {
    orgId: { type: String, required: true },
  },
  setup(props) {
    const controller = inject.controller();
    const router = inject.router();
    const integrations = inject.integrations();
    const route = useRoute();
    const orgId = toRef(props, "orgId");
    const releaser = useReleaser();

    const google = useIntegrationsGoogle(orgId, router);
    const { items: unavailableItems } = useUnavailableIntegrations();

    // All the Membrane data comes from the app-wide integrations store's
    // cache, so building the list per mount is cheap (two local controller
    // RPCs for the native integrations).
    const appList = useMembraneList(controller, router, integrations, orgId);
    releaser.defer(appList.release);

    const isPersonalOrg = computed(() => appList.isPersonalOrg.value);

    const tabTitles = computed<readonly IntegrationTab[]>(() =>
      isPersonalOrg.value ? personalOrgTabTitles : teamOrgTabTitles
    );

    const selectedTab = ref<IntegrationTab>(queryToTab(route.query.nature));

    const selectTab = (tab: string) => {
      selectedTab.value =
        isIntegrationTab(tab) && tabTitles.value.includes(tab)
          ? tab
          : defaultTab;
    };

    // The selected tab can point to a hidden category (e.g. "Calendars"
    // restored from storage or the query while viewing a team org): fall
    // back to the first available tab instead of showing an empty selection.
    const activeTab = computed<IntegrationTab>(() =>
      tabTitles.value.includes(selectedTab.value)
        ? selectedTab.value
        : tabTitles.value[0]
    );

    const activeNature = computed<IntegrationNature | null>(() =>
      tabToNature(activeTab.value)
    );

    const searchQuery = ref("");
    const matchesQuery = (item: { name: string; description?: string }) => {
      const query = searchQuery.value.trim().toLowerCase();
      if (!query) return true;
      return (
        item.name.toLowerCase().includes(query) ||
        (item.description ?? "").toLowerCase().includes(query)
      );
    };

    // Snapshot of the servers listed on mcpservers.org, so a search for e.g.
    // "blender" surfaces the MCP entry point. Loaded lazily on the first
    // keystroke (it is a ~650KB chunk nobody should pay for up front).
    const mcpCatalog = ref<McpCatalogServer[] | null>(null);
    watch(searchQuery, (query) => {
      if (!query.trim() || mcpCatalog.value) return;
      void loadMcpServersCatalog().then((catalog) => {
        mcpCatalog.value = catalog;
      });
    });
    const mcpServerMatches = computed(() =>
      mcpCatalog.value
        ? searchMcpServersCatalog(mcpCatalog.value, searchQuery.value)
        : []
    );

    const infoText = computed(() => {
      if (activeTab.value === "AI") {
        return natureDescriptions.ai;
      }
      if (activeNature.value) {
        return natureDescriptions[activeNature.value];
      }
      return undefined;
    });

    const sortByName = (a: { name: string }, b: { name: string }) =>
      a.name.localeCompare(b.name);

    const connectedItems = computed(() =>
      [
        ...(isPersonalOrg.value ? google.connectedItems.value : []),
        ...appList.connectedItems.value,
      ].sort(sortByName)
    );

    // Entry point to the custom MCP servers screen. Individual servers are
    // not listed here, but the search matches the mcpservers.org catalog:
    // when it is the only reason the card matches, the description names the
    // matching servers so the result does not look arbitrary.
    const mcpItem = computed<IntegrationItem>(() => {
      const base: IntegrationItem = {
        id: "mcp",
        name: "MCP",
        description:
          "Connect your AI agents to any Model Context Protocol server.",
        type: "mcp",
        nature: "ai",
        iconComponent: McpIcon,
        onClick: () => navigateTo(RouteNames.SettingsCustomMcpClients, router),
      };
      const matches = mcpServerMatches.value;
      if (matches.length === 0 || matchesQuery(base)) return base;
      const names = matches.slice(0, 3).map((server) => server.name);
      const more = matches.length - names.length;
      return {
        ...base,
        description: `Matching servers on mcpservers.org: ${names.join(", ")}${
          more > 0 ? ` and ${more} more` : ""
        }.`,
      };
    });

    const availableItems = computed(() =>
      [
        ...(isPersonalOrg.value ? google.availableItems.value : []),
        ...appList.availableItems.value,
        mcpItem.value,
      ].sort(sortByName)
    );

    const allItems = computed(() => {
      const merged = [
        ...connectedItems.value.map((item) => ({ ...item, connected: true })),
        ...availableItems.value.map((item) => ({
          ...item,
          connected: false,
        })),
        ...unavailableItems
          .filter(
            (item) => isPersonalOrg.value || natureByType[item.type] === "ai"
          )
          .map((item) => ({ ...item, connected: false })),
      ];
      const inTab =
        activeTab.value === "AI"
          ? // Membrane apps plus the MCP entry point (not individual servers).
            merged.filter(
              (item) => item.type === "membrane" || item.type === "mcp"
            )
          : merged.filter((item) => natureFor(item) === activeNature.value);
      return inTab.filter(
        (item) =>
          matchesQuery(item) ||
          // The MCP entry point also matches through the mcpservers.org
          // catalog: any server the user could connect makes it a result.
          (item.type === "mcp" && mcpServerMatches.value.length > 0)
      );
    });

    const natureFor = (
      item: Pick<IntegrationItem, "type" | "nature">
    ): IntegrationNature => item.nature ?? natureByType[item.type] ?? "ai";

    return {
      infoText,
      allItems,
      appListError: computed(() => appList.error.value),
      appListLoading: computed(() => appList.loading.value),
      activeTab,
      selectTab,
      tabTitles,
      searchQuery,
    };
  },
});
</script>

<style scoped lang="scss">
@use "../../styles/settings.scss";

.tabs-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
}

.integrations-search {
  width: 220px;
  flex-shrink: 0;

  :deep(.slotLeft) {
    display: flex;
    align-items: center;
  }

  :deep(svg) {
    display: block;
    width: 16px;
    height: 16px;

    .fill {
      @include themed() {
        fill: t($grey-40);
      }
    }
    .stroke {
      @include themed() {
        stroke: t($grey-40);
      }
    }
  }
}

.no-results {
  @extend %font-body-regular;
  margin-top: 24px;

  @include themed() {
    color: t($grey-40);
  }
}

.info-text {
  margin-top: 12px;
  line-height: 1.6;
}

.integration-error {
  margin-top: 12px;
}

.card-grid {
  margin-top: 16px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.card-skeleton {
  height: 70px;
  border-radius: $border-radius-l;
}

.card {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 16px;
  border-radius: $border-radius-l;
  @include transition(all 150ms ease-out);

  @include themed() {
    border: 1px solid t($grey-10);
  }

  &:hover {
    @include themed() {
      border-color: t($grey-20);
    }
  }

  &.connected {
    border: 1px solid rgba($calendar-e-80, 0.2);
    background-color: rgba($calendar-e-80, 0.1);

    &:hover {
      border-color: rgba($calendar-e-80, 0.8);
    }
  }

  &.unavailable {
    opacity: 0.55;
    cursor: default;

    &:hover {
      @include themed() {
        background-color: transparent;
        border-color: t($grey-10);
      }
    }
  }
}

.card-header {
  margin-bottom: 12px;
}

.card-icon {
  height: 28px;
  width: 28px;
  border-radius: $border-radius-m;
}

.card-name {
  @extend %font-body-medium;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;

  @include themed() {
    color: t($primary);
  }
}

.card-description {
  @extend %font-small-regular;
  font-size: 12px;

  @include themed() {
    color: t($grey-50);
  }
}

.card-help {
  position: absolute;
  bottom: 8px;
  inset-inline-end: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  text-decoration: none;
  border-radius: 50%;
  @include transition(all 150ms ease-out);

  @include themed() {
    color: t($grey-80);
    background-color: t($grey-10);
  }

  &:hover {
    @include themed() {
      background-color: t($grey-20);
    }
  }
}
</style>
